import { useEffect, useRef } from 'react';
import noop from 'lodash/noop';
import { useRouterActions } from '@atlassian/react-resource-router';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';

/**
 * This hook can be used to open a modal when the user tries to navigate away from a page.
 */
export const useNavigationBlockModal = (
	shouldModalBeShown: boolean,
	openModal: EntryPointActions['load'],
) => {
	const { registerBlock } = useRouterActions();
	const promiseInfo = useRef<{
		resolve: (shouldResolve: boolean) => void;
	}>({
		resolve: () => noop,
	});
	// This ref protects against the modal reopening when closeModal is called due to registerBlock running again
	const closed = useRef<boolean>(false);

	useEffect(() => {
		const unregister = registerBlock(() => {
			if (shouldModalBeShown && !closed.current) {
				return new Promise((resolve) => {
					promiseInfo.current = {
						resolve,
					};
					openModal();
				});
			}
			return true;
		});

		return () => {
			unregister();
		};
	}, [registerBlock, shouldModalBeShown, openModal]);

	const continueNavigation = () => {
		closed.current = true;
		promiseInfo.current.resolve(true);
	};

	return { continueNavigation };
};
